import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import { useEffect } from 'react';

/**
 * Encapsulates the animated border gradient logic for cell selection.
 * Sets CSS custom properties for cell selection boundaries and animates the gradient angle.
 * - Finds cells with isRovoSelected class
 * - Computes bounding box coordinates for the entire selection
 * - Sets CSS custom properties for positioning the gradient overlay
 * - Animates a CSS custom property `--angle` using requestAnimationFrame
 * - Handles both single and multi-cell selections with unified positioning
 */
export function useRovoGradientAnimationForCells(
  ref: React.RefObject<HTMLElement>,
  isEnabled: boolean,
  selectionKey?: string // Add a dependency that changes when selection changes
) {
  useEffect(() => {
    if (!isEnabled || !ref.current) {
      return;
    }

    const container = ref.current as HTMLElement;

    // Find all selected cells with the isRovoSelected class
    const selectedCells = Array.from(
      container.querySelectorAll(`.${TABLE_CLASSNAMES.IS_ROVO_SELECTED}`)
    ) as HTMLElement[];

    if (selectedCells.length === 0) {
      return;
    }

    // Calculate the bounding box that encompasses all selected cells
    const containerRect = container.getBoundingClientRect();
    const cellRects = selectedCells.map((cell) => cell.getBoundingClientRect());

    let minLeft = cellRects[0].left;
    let maxRight = cellRects[0].right;
    let minTop = cellRects[0].top;
    let maxBottom = cellRects[0].bottom;

    for (let i = 1; i < cellRects.length; i++) {
      const rect = cellRects[i];
      minLeft = Math.min(minLeft, rect.left);
      maxRight = Math.max(maxRight, rect.right);
      minTop = Math.min(minTop, rect.top);
      maxBottom = Math.max(maxBottom, rect.bottom);
    }

    const left = minLeft - containerRect.left;
    const top = minTop - containerRect.top;
    const width = maxRight - minLeft;
    const height = maxBottom - minTop;

    container.style.setProperty('--rovo-visibility', `visible`);
    container.style.setProperty('--rovo-cell-left', `${left}px`);
    container.style.setProperty('--rovo-cell-top', `${top}px`);
    container.style.setProperty('--rovo-cell-width', `${width}px`);
    container.style.setProperty('--rovo-cell-height', `${height}px`);

    // Animate the gradient angle
    let frame = 0;
    let angle = 0;
    const animate = () => {
      angle = (angle + 1) % 360;
      container.style.setProperty('--angle', `${angle}deg`);
      frame = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(frame);
      container.style.setProperty('--rovo-visibility', `hidden`);
      container.style.removeProperty('--rovo-cell-left');
      container.style.removeProperty('--rovo-cell-top');
      container.style.removeProperty('--rovo-cell-width');
      container.style.removeProperty('--rovo-cell-height');
      container.style.removeProperty('--angle');
    };
  }, [isEnabled, ref, selectionKey]);
}

export default useRovoGradientAnimationForCells;
